import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { AutoLaunchAgentSetting } from '../../../../types/aiTerminalSettings';
import { PersistentWorkspacesSection } from './PersistentWorkspacesSection';

type SettingsRowProps = {
  title: string;
  description: string;
  htmlFor: string;
  children: ReactNode;
  testId?: string;
};

function SettingsRow({ title, description, htmlFor, children, testId }: SettingsRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-background p-4" data-testid={testId}>
      <div className="grid gap-0.5">
        <Label htmlFor={htmlFor} className="cursor-pointer">
          {title}
        </Label>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      {children}
    </div>
  );
}

function useNumberInput(value: number) {
  const [input, setInput] = useState<string>(() => String(value));

  useEffect(() => {
    setInput(String(value));
  }, [value]);

  const parsed = useMemo(() => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const next = Number(trimmed);
    if (!Number.isFinite(next)) return null;
    return next;
  }, [input]);

  return { input, setInput, parsed };
}

export function AiTerminalSettingsTab() {
  const settings = useAppStore((state) => state.aiTerminalSettings);
  const setAiTerminalSettings = useAppStore((state) => state.setAiTerminalSettings);
  const resetAiTerminalSettings = useAppStore((state) => state.resetAiTerminalSettings);
  const { track } = useAnalytics();

  const [folderInput, setFolderInput] = useState(settings.aiWorkspace.folder);
  useEffect(() => setFolderInput(settings.aiWorkspace.folder), [settings.aiWorkspace.folder]);

  const syncDebounce = useNumberInput(settings.aiWorkspace.syncDebounceMs);
  const lockTimeout = useNumberInput(settings.aiWorkspace.lockTimeoutMinutes);
  const historyMaxEntries = useNumberInput(settings.history.maxEntries);

  const [historyMaxAgeInput, setHistoryMaxAgeInput] = useState(settings.history.maxAge);
  useEffect(() => setHistoryMaxAgeInput(settings.history.maxAge), [settings.history.maxAge]);

  return (
    <div className="space-y-6">
      <div className="text-xs text-muted-foreground">
        Changes apply immediately and affect new AI terminals; existing sessions keep their current workspace folder.
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">AI Workspace</div>

        <SettingsRow
          title="Workspace folder"
          description="Base folder name under Electron userData (per-connection subfolders are created inside)."
          htmlFor="ai-workspace-folder"
        >
          <Input
            id="ai-workspace-folder"
            className="w-[240px]"
            value={folderInput}
            onChange={(e) => setFolderInput(e.target.value)}
            onBlur={() => setAiTerminalSettings({ aiWorkspace: { folder: folderInput } })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setAiTerminalSettings({ aiWorkspace: { folder: folderInput } });
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="sql-studio-ai-workspace"
            data-testid="ai-workspace-folder-input"
          />
        </SettingsRow>

        <SettingsRow
          title="Sync debounce (ms)"
          description="Debounce delay for writing activeTab.sql/activeSelection.json and syncing working.sql."
          htmlFor="ai-workspace-sync-debounce"
        >
          <Input
            id="ai-workspace-sync-debounce"
            className="w-[120px] text-right"
            inputMode="numeric"
            value={syncDebounce.input}
            onChange={(e) => syncDebounce.setInput(e.target.value)}
            onBlur={() => {
              if (syncDebounce.parsed === null) return;
              setAiTerminalSettings({ aiWorkspace: { syncDebounceMs: syncDebounce.parsed } });
            }}
            data-testid="ai-workspace-sync-debounce-input"
          />
        </SettingsRow>

        <SettingsRow
          title="Lock timeout (minutes)"
          description='After this many minutes of inactivity, show "Force unlock" as a recovery action.'
          htmlFor="ai-workspace-lock-timeout"
        >
          <Input
            id="ai-workspace-lock-timeout"
            className="w-[120px] text-right"
            inputMode="numeric"
            value={lockTimeout.input}
            onChange={(e) => lockTimeout.setInput(e.target.value)}
            onBlur={() => {
              if (lockTimeout.parsed === null) return;
              setAiTerminalSettings({ aiWorkspace: { lockTimeoutMinutes: lockTimeout.parsed } });
            }}
            data-testid="ai-workspace-lock-timeout-input"
          />
        </SettingsRow>

        <SettingsRow
          title="Cleanup on close"
          description="Delete the AI workspace folder when the AI terminal session closes."
          htmlFor="ai-workspace-cleanup-on-close"
        >
          <Switch
            id="ai-workspace-cleanup-on-close"
            checked={settings.aiWorkspace.cleanupOnClose}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ aiWorkspace: { cleanupOnClose: checked } });
              track('feature:ai_terminal_cleanup_on_close_toggled', { enabled: checked });
            }}
            data-testid="ai-workspace-cleanup-on-close-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Export schema on open"
          description="Auto-export schema DDLs to .schema/ when the AI workspace opens."
          htmlFor="ai-workspace-export-schema"
        >
          <Switch
            id="ai-workspace-export-schema"
            checked={settings.aiWorkspace.exportSchemaOnOpen}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ aiWorkspace: { exportSchemaOnOpen: checked } });
              track('feature:ai_terminal_export_schema_toggled', { enabled: checked });
            }}
            data-testid="ai-workspace-export-schema-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Confirm before setting up a workspace"
          description="Ask before exporting schema when opening an AI workspace."
          htmlFor="ai-workspace-confirm-before-setup"
        >
          <Switch
            id="ai-workspace-confirm-before-setup"
            checked={settings.aiWorkspace.confirmBeforeSetup}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ aiWorkspace: { confirmBeforeSetup: checked } });
              track('feature:ai_terminal_confirm_before_setup_toggled', { enabled: checked });
            }}
            data-testid="ai-workspace-confirm-before-setup-toggle"
          />
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">History</div>

        <SettingsRow
          title="Enable history"
          description="Save executed SQL + results to .history/ for AI to read."
          htmlFor="ai-history-enabled"
        >
          <Switch
            id="ai-history-enabled"
            checked={settings.history.enabled}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ history: { enabled: checked } });
              track('feature:ai_terminal_history_enabled_toggled', { enabled: checked });
            }}
            data-testid="ai-history-enabled-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Max entries"
          description="Maximum history entries per AI workspace."
          htmlFor="ai-history-max-entries"
        >
          <Input
            id="ai-history-max-entries"
            className="w-[120px] text-right"
            inputMode="numeric"
            disabled={!settings.history.enabled}
            value={historyMaxEntries.input}
            onChange={(e) => historyMaxEntries.setInput(e.target.value)}
            onBlur={() => {
              if (historyMaxEntries.parsed === null) return;
              setAiTerminalSettings({ history: { maxEntries: historyMaxEntries.parsed } });
            }}
            data-testid="ai-history-max-entries-input"
          />
        </SettingsRow>

        <SettingsRow
          title="Max age"
          description="Retention window (e.g. 7d, 24h, 60m)."
          htmlFor="ai-history-max-age"
        >
          <Input
            id="ai-history-max-age"
            className="w-[120px] text-right"
            disabled={!settings.history.enabled}
            value={historyMaxAgeInput}
            onChange={(e) => setHistoryMaxAgeInput(e.target.value)}
            onBlur={() => setAiTerminalSettings({ history: { maxAge: historyMaxAgeInput } })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setAiTerminalSettings({ history: { maxAge: historyMaxAgeInput } });
                (e.target as HTMLInputElement).blur();
              }
            }}
            data-testid="ai-history-max-age-input"
          />
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Agent Context</div>

        <SettingsRow
          title="Generate context files"
          description="Write CLAUDE.md/AGENTS.md with connection + workspace instructions."
          htmlFor="ai-agent-context-enabled"
        >
          <Switch
            id="ai-agent-context-enabled"
            checked={settings.agentContext.enabled}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ agentContext: { enabled: checked } });
              track('feature:ai_terminal_agent_context_enabled_toggled', { enabled: checked });
            }}
            data-testid="ai-agent-context-enabled-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Include schema summary"
          description="Include a table/column summary in CLAUDE.md (DDL files remain under .schema/)."
          htmlFor="ai-agent-context-include-schema"
        >
          <Switch
            id="ai-agent-context-include-schema"
            checked={settings.agentContext.includeSchema}
            disabled={!settings.agentContext.enabled}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ agentContext: { includeSchema: checked } });
              track('feature:ai_terminal_agent_context_include_schema_toggled', { enabled: checked });
            }}
            data-testid="ai-agent-context-include-schema-toggle"
          />
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Auto-launch</div>

        <SettingsRow
          title="Auto-launch agent on open"
          description="Write the agent's launch command into a freshly opened AI terminal instead of leaving a blank prompt."
          htmlFor="ai-autolaunch-enabled"
        >
          <Switch
            id="ai-autolaunch-enabled"
            checked={settings.autoLaunch.enabled}
            onCheckedChange={(checked) => {
              setAiTerminalSettings({ autoLaunch: { enabled: checked } });
              track('feature:ai_terminal_autolaunch_toggled', { enabled: checked });
            }}
            data-testid="ai-autolaunch-enabled-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Agent"
          description={'"Auto" launches Claude Code when detected, otherwise Codex. An explicit choice is skipped (not substituted) if that CLI isn\'t installed.'}
          htmlFor="ai-autolaunch-agent"
        >
          <Select
            value={settings.autoLaunch.agent}
            onValueChange={(value) => {
              const agent = value as AutoLaunchAgentSetting;
              setAiTerminalSettings({ autoLaunch: { agent } });
              track('feature:ai_terminal_autolaunch_agent_changed', { agent });
            }}
            disabled={!settings.autoLaunch.enabled}
          >
            <SelectTrigger className="w-[160px]" id="ai-autolaunch-agent" data-testid="ai-autolaunch-agent-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (detected)</SelectItem>
              <SelectItem value="claude">Claude Code</SelectItem>
              <SelectItem value="codex">Codex</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>
      </div>

      <PersistentWorkspacesSection />

      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            resetAiTerminalSettings();
            track('feature:ai_terminal_settings_reset');
          }}
          data-testid="ai-terminal-reset-defaults"
        >
          Reset to defaults
        </Button>
      </div>
    </div>
  );
}
